import React, { useEffect, useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter } from './modal';
import { Button } from './button';
import { Input } from './input';
import { Label } from './label';
import FileUpload from './FileUpload';
import { userAPI } from '../../services/api';
import { courseAPI } from '../../services/api';

const CreateUserModal = ({ isOpen, onClose, onUserCreated, userBasicData }) => {
  const [formData, setFormData] = useState({
      email : '',
      student_number: '',
      last_name: '',
      first_name: '',
      rd: '',
      phone_number: '',
      course: '',
      level: '',
      mode: '',
      class: '',
      payment: 0,
      payment_remaining: 0,
      student_id: '',
      initials: '',
      net_id: '',
      combined_name: '',
      class_id: '',
      exam_date: '',
      exam_result: '',
      image: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [courses, setCourses] = useState([]);
  const fetchCourses = async () => {
          try {
            const response = await courseAPI.getAllCourses();
            setCourses(response.data || []);
          } catch (error) {
            console.error('Error fetching courses:', error);
            setCourses([]);
          }
        };

    const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleImageUploadSuccess = (url, key) => {
    console.log('Image uploaded successfully:', url);
    setFormData(prev => ({ ...prev, image: url }));
  };

  const handleImageUploadError = (error) => {
    console.error('Image upload failed:', error);
    setError(`Image upload failed: ${error}`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    console.log('User Profile Data : ',formData);
    try {
      formData.course = String(formData.course);
      formData.payment = parseInt(formData.payment);
      formData.payment_remaining = parseInt(formData.payment_remaining);
      const now = new Date();
      formData.exam_date = formData.exam_date || now.toISOString().split('T')[0];
      await userAPI.createUserProfile(formData);
      onUserCreated();
      onClose();
      setFormData({
      email : '',
      student_number: '',
      last_name: '',
      first_name: '',
      rd: '',
      phone_number: '',
      course: '',
      level: '',
      mode: '',
      class: '',
      payment: 0,
      payment_remaining: 0,
      student_id: '',
      initials: '',
      net_id: '',
      combined_name: '',
      class_id: '',
      exam_date: '',
      exam_result: '',
      image: '',
  });
    } catch (err) {
      setError(err.message || 'Failed to create user');
      console.error('Error creating user:', err);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  useEffect(() => {
    if (isOpen) {
      fetchCourses();
      
      // Populate form with basic user data if provided
      if (userBasicData) {
        setFormData(prev => ({
          ...prev,
          email: userBasicData.email || '',
          first_name: userBasicData.first_name || '',
          last_name: userBasicData.last_name || ''
        }));
      }
    }
  }, [isOpen, userBasicData]);


  const handleClose = () => {
    setFormData({
      email : '',
      student_number: '',
      last_name: '',
      first_name: '',
      rd: '',
      phone_number: '',
      course: '',
      level: '',
      mode: '',
      class: '',
      payment: '0',
      payment_remaining: '0',
      student_id: '',
      initials: '',
      net_id: '',
      combined_name: '',
      class_id: '',
      exam_date: '',
      exam_result: '',
      image: '',
  });
    setError(null);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} className="max-w-4xl max-h-[90vh] flex flex-col">
      <form onSubmit={handleSubmit} className="flex flex-col h-full">
        <ModalHeader className="flex-shrink-0">
          <h2 className="text-xl font-semibold text-gray-900 mb-5">Create User Profile</h2>
          <h3 className="text-lg text-gray-600">Step 2 of 2</h3>
        </ModalHeader>
        
        <ModalBody className="space-y-4 flex-1 overflow-y-auto max-h-[60vh]">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
          {/* Image Upload Section */}
          <div>
            <Label htmlFor="user_image">Profile Image</Label>
            <div className="mt-1">
              <FileUpload
                onUploadSuccess={handleImageUploadSuccess}
                onUploadError={handleImageUploadError}
                accept="image/*"
                maxSize={10 * 1024 * 1024} // 10MB for user images
                folder="user-profiles"
                disabled={isSubmitting}
                showPreview={true}
                showFileBrowser={true}
                currentUrl={formData.image}
                className="w-full"
              />
              <p className="mt-1 text-sm text-gray-500">
                Upload a profile image (optional). Supported formats: JPG, PNG, GIF. Max size: 10MB.
              </p>
              {formData.image && (
                <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded">
                  <p className="text-sm text-green-700">
                    Image uploaded: <a href={formData.image} target="_blank" rel="noopener noreferrer" className="underline">View image</a>
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Email and Phone Number - Always visible */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="text"
                value={formData.email || ''}
                onChange={(e) => handleInputChange('email', e.target.value)}
                required
                disabled={isSubmitting || userBasicData?.email}
                className={userBasicData?.email ? 'bg-gray-100' : ''}
              />
              {userBasicData?.email && (
                <p className="mt-1 text-xs text-gray-500">
                  Email filled from previous step
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="phone_number">Phone Number</Label>
              <Input
                id="phone_number"
                type="text"
                value={formData.phone_number || ''}
                onChange={(e) => handleInputChange('phone_number', e.target.value)}
                required
                disabled={isSubmitting}
              />
            </div>
          </div>

          {/* First Name and Last Name - Always visible */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="first_name">First Name</Label>
              <Input
                id="first_name"
                type="text"
                value={formData.first_name || ''}
                onChange={(e) => handleInputChange('first_name', e.target.value)}
                required
                disabled={isSubmitting || userBasicData?.first_name}
                className={userBasicData?.first_name ? 'bg-gray-100' : ''}
              />
              {userBasicData?.first_name && (
                <p className="mt-1 text-xs text-gray-500">
                  First name filled from previous step
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="last_name">Last Name</Label>
              <Input
                id="last_name"
                type="text"
                value={formData.last_name || ''}
                onChange={(e) => handleInputChange('last_name', e.target.value)}
                required
                disabled={isSubmitting || userBasicData?.last_name}
                className={userBasicData?.last_name ? 'bg-gray-100' : ''}
              />
              {userBasicData?.last_name && (
                <p className="mt-1 text-xs text-gray-500">
                  Last name filled from previous step
                </p>
              )}
            </div>
          </div>

          {/* Conditional fields - Hide for instructor role */}
          {userBasicData?.role !== 'instructor' && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="student_number">Student Number</Label>
                  <Input
                    id="student_number"
                    type="text"
                    value={formData.student_number|| ''}
                    onChange={(e) => handleInputChange('student_number', e.target.value)}
                    required
                    disabled={isSubmitting}
                  />
                </div>
                <div>
                  <Label htmlFor="rd">Register number</Label>
                  <Input
                    id="rd"
                    type="text"
                    value={formData.rd || ''}
                    onChange={(e) => handleInputChange('rd', e.target.value)}
                    required
                    disabled={isSubmitting}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                   <label htmlFor="course" className="block text-sm font-medium text-gray-700 mb-1">
                     Course *
                   </label>
                   <select
                     id="course"
                     name="course"
                     value={formData.course || ''}
                     onChange={(e) => handleInputChange('course', e.target.value)}
                     required
                     className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select a course</option>
                    {courses.map((course) => (
                      <option key={course.id} value={course.id}>
                        {course.title}
                      </option>
                    ))}
                  </select>
                 </div>
                <div>
                  <Label htmlFor="level">Level</Label>
                  <Input
                    id="level"
                    name="level"
                    value={formData.level || ''}
                    onChange={(e) => handleInputChange('level', e.target.value)}
                    required
                    disabled={isSubmitting}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="mode">Mode</Label>
                  <select
                    id="mode"
                    name="mode"
                    value={formData.mode}
                    onChange={(e) => handleInputChange('mode', e.target.value)}
                    required
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <option value="">Select a mode</option>
                    <option value="Offline">Offline</option>
                    <option value="Online">Online</option>
                    <option value="Hybrid">Hybrid</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="initials">Initials</Label>
                  <Input
                    id="initials"
                    type="text"
                    value={formData.initials || ''}
                    onChange={(e) => handleInputChange('initials', e.target.value)}
                    required
                    disabled={isSubmitting}
                  />
                </div>
                {/* <div>
                  <Label htmlFor="class">Class</Label>
                  <Input
                    id="class"
                    type="text"
                    value={formData.class || ''}
                    onChange={(e) => handleInputChange('class', e.target.value)}
                    required
                    disabled={isSubmitting}
                  />
                </div> */}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="payment">Payment</Label>
                  <Input
                    id="payment"
                    type="number"
                    value={formData.payment || '0'}
                    onChange={(e) => handleInputChange('payment', e.target.value)}
                    required
                    disabled={isSubmitting}
                  />
                </div>
                <div>
                  <Label htmlFor="payment_remaining">Payment Remaining</Label>
                  <Input
                    id="payment_remaining"
                    type="number"
                    value={formData.payment_remaining || '0'}
                    onChange={(e) => handleInputChange('payment_remaining', e.target.value)}
                    required
                    disabled={isSubmitting}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {/* <div>
                  <Label htmlFor="student_id">Student ID</Label>
                  <Input
                    id="student_id"
                    type="text"
                    value={formData.student_id || ''}
                    onChange={(e) => handleInputChange('student_id', e.target.value)}
                    required
                    disabled={isSubmitting}
                  />
                </div> */}
                
              </div>  
              <div className="grid grid-cols-2 gap-4">
                {/* <div>
                  <Label htmlFor="net_id">Net ID</Label>
                  <Input
                    id="net_id"
                    type="text"
                    value={formData.net_id || ''}
                    onChange={(e) => handleInputChange('net_id', e.target.value)}
                    required
                    disabled={isSubmitting}
                  />
                </div> */}
                {/* <div>
                  <Label htmlFor="combined_name">Combined Name</Label>
                  <Input
                    id="combined_name"
                    type="text"
                    value={formData.combined_name || ''}
                    onChange={(e) => handleInputChange('combined_name', e.target.value)}
                    required
                    disabled={isSubmitting}
                  />
                </div> */}
                <div>
                  <Label htmlFor="exam_date">Exam Date</Label>
                  <Input
                    id="exam_date"
                    type="date"
                    value={formData.exam_date || '2025-12-31'}
                    onChange={(e) => handleInputChange('exam_date', e.target.value)}
                    disabled={isSubmitting}
                  />
                </div>
                <div>
                  <Label htmlFor="exam_result">Exam Result</Label>
                  <Input
                    id="exam_result"
                    type="text"
                    value={formData.exam_result || ''}
                    onChange={(e) => handleInputChange('exam_result', e.target.value)}
                    required
                    disabled={isSubmitting}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {/* <div>
                  <Label htmlFor="class_id">Class ID</Label>
                  <Input
                    id="class_id"
                    type="text"
                    value={formData.class_id || ''}
                    onChange={(e) => handleInputChange('class_id', e.target.value)}
                    required
                    disabled={isSubmitting}
                  />
                </div> */}
                
              </div>
              <div className="grid grid-cols-2 gap-4">
              </div>
            </>
          )}
        </ModalBody>
        
        <ModalFooter className="flex-shrink-0">
          <Button 
            type="button" 
            variant="outline" 
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Creating...' : 'Create User'}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default CreateUserModal;